import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, ViewEncapsulation } from '@angular/core';
import { WorkItemTypes } from 'common/workitems/constants/workitem-types';
import { bcEnums } from 'common/common/constants/boldchat-enums';
import { BcVisitorInfoBuilderService } from 'components/work-item-lane-list/work-item-lane/work-item-widget/shared/services/visitor-info-builder.service';
import { VisitorInfoModel } from 'shared/models/visitor-info/visitor-info.model';
import { Observable, of, combineLatest, timer, from } from 'rxjs';
import { switchMap, map, startWith, debounceTime } from 'rxjs/operators';
import { CustomerInformationHubService } from 'common/api/customer-information-hub.service';
import { BcSettingsService } from 'common/settings/settings.service';
import { BcFeatures } from 'common/common/constants/feature-enums';
import { AggregationData, CustomFields, Visit, Visitor, ExperimentData } from 'shared/components/detailed-data/visit.interface';
import { visitorInfoBlockDefaultStates } from 'common/common/constants/visitor-info-block-default-states';
import { BcRestApi } from 'common/api/rest-api';
import { CriterionGroup } from 'shared/models/visitor-info/criterion-group';

const outReachLabels = ['conversionPurchasedAmount', 'conversionNonPurchasedAmount', 'conversionPurchasedAmountCount', 'conversionNonPurchasedAmountCount',
  'conversionAmountSum', 'conversionCount', 'invitationCount', 'acceptedInvitationCount', 'ignoredInvitationCount', 'declinedInvitationCount'];
const visitHistoryLabels = ['visitCount', 'visitLengthSum', 'visitLengthAvg'];
const MINUTE_IN_MS = 60000;

@Component({
  selector: 'app-detailed-data',
  templateUrl: './detailed-data.component.html',
  styleUrls: ['./detailed-data.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class DetailedDataComponent implements OnChanges, OnDestroy {
  @Input()
  workItemId: string;

  @Input()
  workItemType: WorkItemTypes;

  @Input()
  isNotAvailableDataHidden: boolean;

  public visitor$: Observable<VisitorInfoModel>;
  public bcEnums;
  public blockDefaultOpenStates;

  private _initialized = false;
  private _cihRealTimeUpdateEnabled = false;
  private _visitId = null;
  private _visitorId = null;

  constructor(private _visitorInfoBuilder: BcVisitorInfoBuilderService,
              private _changeDetector: ChangeDetectorRef,
              private _bcSettingsService: BcSettingsService,
              private _customerInformationHubService: CustomerInformationHubService,
              private bcRestApi: BcRestApi) {
    this.bcEnums = bcEnums;
    this.blockDefaultOpenStates = visitorInfoBlockDefaultStates;
  }

  async ngOnChanges() {
    await this._initialize();

    if (this.workItemType && this.workItemId) {
      this.visitor$ = this._getVisitorObservable();
      this._changeDetector.detectChanges();
    }
  }

  ngOnDestroy() {
    if (this._cihRealTimeUpdateEnabled && this.isChat()) {
      this._customerInformationHubService.unsubscribeRealTimeData(this._visitorId, this._visitId);
    }
  }

  public getDetailedDataObjectKeys(detailedDataObj) {
    if (detailedDataObj) {
      return Object.keys(detailedDataObj);
    }
  }

  public trackByObjectKey(index, key): string {
    return key;
  }

  public isChat(): boolean {
    return this.workItemType === WorkItemTypes.CHAT;
  }

  public blockStateToggled(blockName, newState) {
    this.blockDefaultOpenStates[blockName] = newState;
  }

  _getVisitorObservable(): Observable<VisitorInfoModel> {
    let visitorObservable;

    if (this._cihRealTimeUpdateEnabled && this.isChat()) {
      visitorObservable = this._visitorInfoBuilder.observeVisitorData(this.workItemId, this.workItemType).pipe(
        switchMap<VisitorInfoModel, Observable<VisitorInfoModel>>(visitorInfoModel => {
          this._visitorId = visitorInfoModel.detailedData.general.visitorId.value.toString();
          this._visitId = visitorInfoModel.detailedData.info.visitId.value.toString();
          this._customerInformationHubService.setRealTimeDataParams(this._visitorId, this._visitId, this.workItemId, this.workItemType);

          return combineLatest([
            of(visitorInfoModel),
            this._customerInformationHubService.subscribeRealTimeData(this._visitorId, this._visitId, this.workItemId, this.workItemType)
              .pipe(startWith(<Observable<[Visitor, Visit, CustomFields, AggregationData]>>null)),
            timer(0, MINUTE_IN_MS)
          ]).pipe(
            debounceTime(1000),
            switchMap(([visitorInfoModel, realTimeData]) => {
              if (realTimeData) {
                return from(this.bcRestApi.getCampaignRules(realTimeData[4]).catch(() => <any>{})).pipe(
                  map(campaignRules => {
                    this._setVisitHistory(visitorInfoModel, realTimeData[1], realTimeData[3]);
                    this._setOutreachDetails(visitorInfoModel, realTimeData[4], campaignRules);
                    this._setOutreachResults(visitorInfoModel, realTimeData[1], realTimeData[3]);
                    this._setCustomFields(visitorInfoModel, realTimeData[2]);

                    return visitorInfoModel;
                  }));
              }
              return of(visitorInfoModel);
            }));
        }));
    } else {
      visitorObservable = this._visitorInfoBuilder.observeVisitorData(this.workItemId, this.workItemType);
    }

    return visitorObservable;
  }

  calculateCurrentVisitLength(created: string): number {
    return Math.floor((new Date().getTime() - new Date(created).getTime()) / MINUTE_IN_MS);
  }

  private _setOutreachResults(visitorData: VisitorInfoModel, visit: Visit, aggregationData: AggregationData): void {
    visitorData.detailedData.outreachResults = outReachLabels.reduce((accumulator, outreachLabel) => ({ ...accumulator, [outreachLabel]: { value: {...visit.data, ...aggregationData.data}[outreachLabel], translateKey: outreachLabel } }), {});
  }

  private _setOutreachDetails(visitorData: VisitorInfoModel, experimentData: ExperimentData, campaignRules: CriterionGroup): void {
    if (experimentData && experimentData.data) {
      visitorData.detailedData.outreachDetails = {
        activityName: { value: experimentData.data.experimentName, visible: true, translateKey: 'outreachActivity' },
        actionName: { value: experimentData.data.actionName, visible: true, translateKey: 'outreachAction' },
        conditions: campaignRules
      };
    }
  }

  private _setCustomFields(visitorData: VisitorInfoModel, { data }): void {
    visitorData.detailedData.customFields = data.reduce((accumulator, { key, value, label }) => ({ ...accumulator, [key]: { value: value, label: label ? label : key } }), {});
  }

  private _setVisitHistory(visitorData: VisitorInfoModel, visit: Visit, aggregationData: AggregationData): void {
    visitorData.detailedData.visitHistory = visitHistoryLabels.reduce((accumulator, visitHistoryLabel) => ({ ...accumulator, [visitHistoryLabel]: { value: aggregationData.data[visitHistoryLabel], translateKey: visitHistoryLabel } }), {});
    visitorData.detailedData.events.currentVisitLength = {
      value: this.calculateCurrentVisitLength(visit.data.created),
      translateKey: 'currentVisitLength',
      visible: true
    };
    if (aggregationData.data.visitLengthSum !== undefined && aggregationData.data.currentVisitLength !== undefined) {
      visitorData.detailedData.visitHistory.visitLengthSum = {
        value: aggregationData.data.visitLengthSum - aggregationData.data.currentVisitLength + <number>visitorData.detailedData.events.currentVisitLength.value,
        translateKey: 'visitLengthSum',
        visible: true
      };
    }
  }

  private async _initialize(): Promise<void> {
    if (this._initialized) {
      return;
    }

    const settings = await this._bcSettingsService.getSettings();
    this._initialized = true;
    this._cihRealTimeUpdateEnabled = this._bcSettingsService.VisitFeatures.isEnabled(settings, BcFeatures.VM_CIH_REAL_TIME_DATA_SYNC) && this._bcSettingsService.VisitFeatures.isEnabled(settings, BcFeatures.VM_CUSTOMER_INFORMATION_HUB);
  }
}
